import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { AnimatePresence, motion } from 'motion/react';
import { format, isSameDay, isSameYear, isToday, isYesterday } from 'date-fns';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  ArrowDown,
  Copy,
  MessagesSquare,
  Pencil,
  Pin,
  PinOff,
  Reply,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  DISCUSSION_REACTIONS,
  DISCUSSION_REACTION_IDS,
  canProject,
  type DiscussionCatchUpResult,
  type DiscussionMessage,
  type DiscussionReactionId,
  type Job,
} from '@allemnii-pulse/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { useProject } from '../projects/ProjectProvider.js';
import { api } from '../../lib/api.js';
import { db } from '../../lib/firebase.js';
import { useDocument } from '../../lib/firestore.js';
import { asDate } from '../../lib/time.js';
import { useUserProfiles } from '../../lib/userProfiles.js';
import { cn } from '../../lib/utils.js';
import { UserName } from '../../components/UserName.js';
import { Button } from '../../components/ui/Button.js';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';
import { BottomSheet, SheetAction } from './BottomSheet.js';
import { Composer, type ComposedMessage, type Participant } from './Composer.js';
import { MessageItem, type PendingState } from './MessageItem.js';
import { mentionName, plainExcerpt } from './MessageBody.js';
import {
  markRead,
  messagesPath,
  useAttachmentUrls,
  useDiscussionMessages,
  useLastRead,
  usePinnedMessages,
} from './data.js';

/** A message sent from this device that the live listener hasn't delivered back yet. */
interface PendingMessage {
  tempId: string;
  composed: ComposedMessage;
  replyToId: string | null;
  createdAt: Date;
  state: PendingState;
  /** Set once the API accepted it; the entry is dropped when that id arrives live. */
  serverId?: string;
}

/** A row in the list: a delivered message, or one of your own still on its way. */
type Row = DiscussionMessage & { pendingTempId?: string; pendingState?: PendingState };

const GROUP_WINDOW_MS = 5 * 60 * 1000;
/** Within this distance of the bottom counts as "reading the latest" — new messages then scroll
 * into view on their own instead of piling up behind the jump button. */
const NEAR_BOTTOM_PX = 120;

function dayLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, isSameYear(date, new Date()) ? 'EEEE, MMM d' : 'MMM d, yyyy');
}

function Divider({ label, accent = false }: { label: string; accent?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 sm:px-5" role="separator" aria-label={label}>
      <span className={cn('h-px flex-1', accent ? 'bg-danger/50' : 'bg-border')} />
      <span className={cn('text-[11px] font-semibold', accent ? 'text-danger' : 'text-muted')}>
        {label}
      </span>
      <span className={cn('h-px flex-1', accent ? 'bg-danger/50' : 'bg-border')} />
    </div>
  );
}

/** The AI "catch me up" digest, run as a job like "Ask this project". */
function CatchUpSheet({
  open,
  onOpenChange,
  projectId,
  since,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  since: Date | null;
}) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);

  useEffect(() => {
    if (!open) return;
    setJobId(null);
    setStartError(null);
    api
      .post<{ jobId: string }>(`/projects/${projectId}/discussions/catch-up`, {
        since: since?.toISOString() ?? null,
      })
      .then((res) => setJobId(res.jobId))
      .catch(() => setStartError("Couldn't start the catch-up. Try again."));
  }, [open]);

  const result = job?.status === 'succeeded' ? (job.result as DiscussionCatchUpResult) : null;
  const failed = job?.status === 'failed' || startError;

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Catch me up">
      <div className="flex flex-col gap-3 px-5 pb-5 pt-2 md:pt-5">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Sparkles className="h-4 w-4 text-accent" /> Catch me up
        </h2>
        {failed && (
          <p className="text-sm text-danger">{startError ?? 'The catch-up failed. Try again.'}</p>
        )}
        {!result && !failed && (
          <div className="flex flex-col gap-2" aria-live="polite">
            <p className="text-sm text-muted">Reading the discussion…</p>
            {[80, 95, 60].map((w) => (
              <span
                key={w}
                className="h-3 animate-pulse rounded bg-surface-2"
                style={{ width: `${w}%` }}
              />
            ))}
          </div>
        )}
        {result && (
          <>
            <p className="text-xs text-muted">
              {result.onlyNew
                ? `${result.messageCount} new message${result.messageCount === 1 ? '' : 's'} since your last visit`
                : result.messageCount
                  ? `Nothing new since your last visit — the latest ${result.messageCount} messages`
                  : ''}
            </p>
            <div
              className="doc-prose max-w-none text-[15px] md:text-sm"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  marked.parse(result.summary, { async: false, gfm: true }),
                ),
              }}
            />
            <p className="text-[11px] text-muted">
              AI summary — check the messages for anything important.
            </p>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

/**
 * The project's discussion — a flat, chat-style thread where members (not viewers) ask for
 * feedback, flag blockers and share files. See packages/shared/src/schemas/discussion.ts. Built
 * phone-first: the page is a full-height column with the message list scrolling between a slim
 * header and the composer, which the app shell keeps above the on-screen keyboard (see
 * lib/viewport.ts).
 */
export function DiscussionsPage() {
  const { projectId, project, role } = useProject();
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const canParticipate = canProject(role, 'discussion.participate');

  const { messages, loading, error, hasMore, loadingOlder, loadOlder, refresh } =
    useDiscussionMessages(projectId);
  const pinned = usePinnedMessages(projectId);
  const urls = useAttachmentUrls(projectId, messages);
  const { lastRead, loading: lastReadLoading } = useLastRead(uid, projectId);

  const participantUids = useMemo(() => {
    const viewers = new Set(project?.viewerUids ?? []);
    return (project?.memberUids ?? []).filter((m) => !viewers.has(m));
  }, [project?.memberUids, project?.viewerUids]);
  const authorUids = useMemo(() => [...new Set(messages.map((m) => m.authorUid))], [messages]);
  const profiles = useUserProfiles(
    useMemo(
      () => [...new Set([...participantUids, ...authorUids])].sort(),
      [participantUids, authorUids],
    ),
  );
  const participants: Participant[] = useMemo(
    () =>
      participantUids
        .filter((p) => p !== uid)
        .map((p) => ({ uid: p, name: mentionName(p, profiles[p]) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [participantUids, profiles, uid],
  );

  // The read marker as it was when the page opened — where "New" goes, and what "catch me up"
  // covers. Captured once: the live marker moves forward as soon as the messages are seen.
  const [entryLastRead, setEntryLastRead] = useState<Date | null | undefined>(undefined);
  useEffect(() => {
    if (entryLastRead === undefined && !lastReadLoading) setEntryLastRead(lastRead);
  }, [lastReadLoading, lastRead, entryLastRead]);

  const [replyTo, setReplyTo] = useState<DiscussionMessage | null>(null);
  const [editing, setEditing] = useState<DiscussionMessage | null>(null);
  const [actionsFor, setActionsFor] = useState<DiscussionMessage | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DiscussionMessage | null>(null);
  const [showPinned, setShowPinned] = useState(false);
  const [showCatchUp, setShowCatchUp] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [reactionOverrides, setReactionOverrides] = useState<
    Record<string, DiscussionMessage['reactions']>
  >({});
  const [parentCache, setParentCache] = useState<Record<string, DiscussionMessage | null>>({});
  const [newBelow, setNewBelow] = useState(0);
  const [atBottom, setAtBottom] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const initialScrollDone = useRef(false);
  const lastCountRef = useRef(0);
  const olderAnchor = useRef<{ height: number; top: number } | null>(null);

  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  // --- read marker: everything on screen counts as read while the page is visible ---
  const newest = messages[messages.length - 1];
  useEffect(() => {
    // Not before the entry marker is captured: an early write would be read back as the marker
    // the page opened with, hiding the "New" divider and emptying "catch me up".
    if (!uid || !newest || !canParticipate || entryLastRead === undefined) return;
    const at = asDate(newest.createdAt);
    const mark = () => {
      if (document.visibilityState !== 'visible') return;
      if (lastRead && lastRead.getTime() >= at.getTime()) return;
      void markRead(uid, projectId, at);
    };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [newest?.id, uid, projectId, lastRead?.getTime(), canParticipate, entryLastRead]);

  // --- pending sends leave once the live listener delivers them ---
  useEffect(() => {
    setPending((list) => {
      const delivered = list.filter((p) => p.serverId && byId.has(p.serverId));
      if (delivered.length === 0) return list;
      delivered.forEach((p) =>
        p.composed.localAttachments.forEach(
          (a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl),
        ),
      );
      return list.filter((p) => !delivered.includes(p));
    });
  }, [byId]);

  // --- an optimistic reaction stands until the server's copy matches it ---
  useEffect(() => {
    setReactionOverrides((overrides) => {
      const next = { ...overrides };
      let changed = false;
      for (const [id, reactions] of Object.entries(overrides)) {
        const server = byId.get(id);
        if (
          !server ||
          JSON.stringify(normalize(server.reactions)) === JSON.stringify(normalize(reactions))
        ) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : overrides;
    });
  }, [byId]);

  // --- quotes of messages older than anything loaded are fetched one by one ---
  useEffect(() => {
    const missing = messages
      .map((m) => m.replyToId)
      .filter((id): id is string => !!id && !byId.has(id) && !(id in parentCache));
    for (const id of new Set(missing)) {
      void getDoc(doc(db, messagesPath(projectId), id)).then((snap) =>
        setParentCache((c) => ({
          ...c,
          [id]: snap.exists() ? (snap.data() as DiscussionMessage) : null,
        })),
      );
    }
  }, [messages, byId]);

  // --- scrolling ---
  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    setNewBelow(0);
  }, []);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    setAtBottom(near);
    if (near) setNewBelow(0);
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('m');
  const [jumpTarget, setJumpTarget] = useState<string | null>(deepLinkId);
  useEffect(() => {
    if (deepLinkId) setJumpTarget(deepLinkId);
  }, [deepLinkId]);

  // First paint: open at the deep-linked message, else the "New" divider, else the bottom.
  useLayoutEffect(() => {
    if (initialScrollDone.current || loading || entryLastRead === undefined) return;
    initialScrollDone.current = true;
    lastCountRef.current = messages.length;
    if (jumpTarget) return;
    const firstUnread = document.getElementById('discussion-new-divider');
    if (firstUnread) firstUnread.scrollIntoView({ block: 'start' });
    else scrollToBottom(false);
  }, [loading, entryLastRead, messages.length]);

  // Later arrivals: follow along when at the bottom or when it's your own; otherwise count them.
  useEffect(() => {
    if (!initialScrollDone.current) return;
    const added = messages.length - lastCountRef.current;
    lastCountRef.current = messages.length;
    if (added <= 0 || olderAnchor.current) return;
    const latest = messages[messages.length - 1];
    if (atBottom || latest?.authorUid === uid) requestAnimationFrame(() => scrollToBottom(true));
    else setNewBelow((n) => n + added);
  }, [messages.length]);

  // Older pages prepend: keep the reader's place instead of jumping.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const anchor = olderAnchor.current;
    if (!el || !anchor || loadingOlder) return;
    el.scrollTop = anchor.top + (el.scrollHeight - anchor.height);
    olderAnchor.current = null;
  }, [messages[0]?.id, loadingOlder]);

  const requestOlder = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || loadingOlder) return;
    olderAnchor.current = { height: el.scrollHeight, top: el.scrollTop };
    void loadOlder();
  }, [hasMore, loadingOlder, loadOlder]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !initialScrollDone.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry?.isIntersecting && requestOlder(),
      { root: scrollRef.current, rootMargin: '300px 0px 0px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [requestOlder, loading]);

  // Jumping to a message (a reply's quote, a pin, a notification): page back until it's loaded.
  useEffect(() => {
    if (!jumpTarget || loading) return;
    const el = document.getElementById(`msg-${jumpTarget}`);
    if (el) {
      el.scrollIntoView({
        block: 'center',
        behavior: initialScrollDone.current ? 'smooth' : 'auto',
      });
      setHighlightId(jumpTarget);
      setJumpTarget(null);
      if (searchParams.has('m')) {
        searchParams.delete('m');
        setSearchParams(searchParams, { replace: true });
      }
      const t = window.setTimeout(() => setHighlightId(null), 2200);
      return () => window.clearTimeout(t);
    }
    if (hasMore && !loadingOlder) void loadOlder();
    else if (!hasMore) {
      setJumpTarget(null);
      scrollToBottom(false);
    }
  }, [jumpTarget, loading, messages.length, hasMore, loadingOlder]);

  // --- actions ---
  async function send(composed: ComposedMessage, existing?: PendingMessage) {
    const entry: PendingMessage = existing
      ? { ...existing, state: 'sending' }
      : {
          tempId: `pending-${Date.now()}`,
          composed,
          replyToId: replyTo?.id ?? null,
          createdAt: new Date(),
          state: 'sending',
        };
    setPending((list) =>
      existing ? list.map((p) => (p.tempId === entry.tempId ? entry : p)) : [...list, entry],
    );
    setReplyTo(null);
    requestAnimationFrame(() => scrollToBottom(true));
    try {
      const { id } = await api.post<{ id: string }>(`/projects/${projectId}/discussions`, {
        body: composed.body,
        mentionedUids: composed.mentionedUids,
        mentionsAll: composed.mentionsAll,
        replyToId: entry.replyToId,
        attachments: composed.attachments,
      });
      setPending((list) =>
        list.map((p) => (p.tempId === entry.tempId ? { ...p, serverId: id } : p)),
      );
    } catch {
      setPending((list) =>
        list.map((p) => (p.tempId === entry.tempId ? { ...p, state: 'failed' } : p)),
      );
    }
  }

  async function saveEdit(
    message: DiscussionMessage,
    edit: Pick<ComposedMessage, 'body' | 'mentionedUids' | 'mentionsAll'>,
  ) {
    await api.patch(`/projects/${projectId}/discussions/${message.id}`, edit);
    await refresh(message.id);
  }

  function react(message: DiscussionMessage, reaction: DiscussionReactionId) {
    const current = reactionOverrides[message.id] ?? message.reactions;
    const uids = current[reaction] ?? [];
    const next = {
      ...current,
      [reaction]: uids.includes(uid) ? uids.filter((u) => u !== uid) : [...uids, uid],
    };
    setReactionOverrides((o) => ({ ...o, [message.id]: next }));
    api
      .post(`/projects/${projectId}/discussions/${message.id}/reactions`, { reaction })
      .then(() => refresh(message.id))
      .catch(() => {
        setReactionOverrides((o) => {
          const { [message.id]: _, ...rest } = o;
          return rest;
        });
        setActionError("Couldn't update your reaction.");
      });
  }

  async function togglePin(message: DiscussionMessage) {
    try {
      await api.post(`/projects/${projectId}/discussions/${message.id}/pin`, {
        pinned: !message.pinnedAt,
      });
      await refresh(message.id);
    } catch {
      setActionError(`Couldn't ${message.pinnedAt ? 'unpin' : 'pin'} that message.`);
    }
  }

  async function deleteMessage(message: DiscussionMessage) {
    setConfirmDelete(null);
    try {
      await api.delete(`/projects/${projectId}/discussions/${message.id}`);
      await refresh(message.id);
      if (replyTo?.id === message.id) setReplyTo(null);
    } catch {
      setActionError("Couldn't delete that message.");
    }
  }

  useEffect(() => {
    if (!actionError) return;
    const t = window.setTimeout(() => setActionError(null), 4000);
    return () => window.clearTimeout(t);
  }, [actionError]);

  // --- render ---
  if (!canParticipate) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted">
        Discussions are open to project members. Viewers don't have access.
      </div>
    );
  }

  const pendingMessages: Row[] = pending
    .filter((p) => !(p.serverId && byId.has(p.serverId)))
    .map((p) => ({
      id: p.tempId,
      authorUid: uid,
      body: p.composed.body,
      mentionedUids: p.composed.mentionedUids,
      mentionsAll: p.composed.mentionsAll,
      notifiedUids: [],
      replyToId: p.replyToId,
      attachments: p.composed.localAttachments.map((a, i) => ({
        id: `${p.tempId}-${i}`,
        storagePath: '',
        name: a.name,
        mime: '',
        kind: a.previewUrl ? 'image' : 'document',
        sizeBytes: 0,
      })),
      reactions: {},
      pinnedAt: null,
      pinnedByUid: null,
      createdAt: p.createdAt.toISOString(),
      editedAt: null,
      pendingTempId: p.tempId,
      pendingState: p.state,
    }));
  const pendingUrls = Object.fromEntries(
    pending.flatMap((p) =>
      p.composed.localAttachments.map((a, i) => [`${p.tempId}-${i}`, a.previewUrl ?? '']),
    ),
  );

  const firstUnreadId =
    entryLastRead === undefined
      ? null
      : (messages.find(
          (m) => m.authorUid !== uid && (!entryLastRead || asDate(m.createdAt) > entryLastRead),
        )?.id ?? null);
  // A brand-new reader would see "New" above the very first message, which says nothing.
  const showNewDivider = !!firstUnreadId && !!entryLastRead;

  const parentOf = (m: DiscussionMessage) =>
    m.replyToId ? (byId.get(m.replyToId) ?? parentCache[m.replyToId]) : undefined;

  const all: Row[] = [...messages, ...pendingMessages];
  const actionsMessage = actionsFor ? (byId.get(actionsFor.id) ?? actionsFor) : null;
  const isOwn = actionsMessage?.authorUid === uid;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-12 flex-shrink-0 items-center gap-1 border-b border-border bg-surface px-3 sm:px-5">
        <h1 className="flex items-center gap-2 text-sm font-semibold">
          <MessagesSquare className="h-4 w-4 text-muted" /> Discussions
        </h1>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            className="h-9 gap-1.5 px-2.5 md:h-8"
            onClick={() => setShowPinned(true)}
            aria-label={`Pinned messages (${pinned.length})`}
          >
            <Pin className="h-4 w-4" />
            <span className="tabular-nums">{pinned.length}</span>
          </Button>
          <Button
            variant="outline"
            className="h-9 gap-1.5 px-2.5 md:h-8"
            onClick={() => setShowCatchUp(true)}
            disabled={messages.length === 0}
          >
            <Sparkles className="h-4 w-4 text-accent" />
            <span>Catch me up</span>
          </Button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-full overflow-y-auto overscroll-contain pb-3"
          aria-label="Messages"
          role="log"
        >
          <div ref={topSentinelRef} />
          {loadingOlder && (
            <p className="py-3 text-center text-xs text-muted">Loading earlier messages…</p>
          )}
          {!hasMore && messages.length > 0 && (
            <p className="px-5 pb-1 pt-6 text-center text-xs text-muted">
              This is the start of the discussion for {project?.name ?? 'this project'}.
            </p>
          )}
          {loading && <p className="py-10 text-center text-sm text-muted">Loading messages…</p>}
          {error && (
            <p className="py-10 text-center text-sm text-danger">Couldn't load the discussion.</p>
          )}
          {!loading && !error && all.length === 0 && (
            <div className="mx-auto flex max-w-sm flex-col items-center gap-2 px-6 py-16 text-center">
              <MessagesSquare className="h-8 w-8 text-muted" />
              <p className="text-sm font-medium">Start the conversation</p>
              <p className="text-sm text-muted">
                Ask for feedback, share a thought, raise a blocker. Type @ to tag someone. Only the
                people you tag get notified.
              </p>
            </div>
          )}

          {all.map((m, i) => {
            const prev = all[i - 1];
            const created = asDate(m.createdAt);
            const newDay = !prev || !isSameDay(asDate(prev.createdAt), created);
            const isPending = !!m.pendingTempId;
            const grouped =
              !!prev &&
              !newDay &&
              prev.authorUid === m.authorUid &&
              !m.replyToId &&
              m.id !== firstUnreadId &&
              created.getTime() - asDate(prev.createdAt).getTime() < GROUP_WINDOW_MS;
            const reactions = reactionOverrides[m.id];
            return (
              <Fragment key={m.pendingTempId ?? m.id}>
                {newDay && <Divider label={dayLabel(created)} />}
                {showNewDivider && m.id === firstUnreadId && (
                  <div id="discussion-new-divider" className="scroll-mt-2">
                    <Divider label="New" accent />
                  </div>
                )}
                <MessageItem
                  message={reactions ? { ...m, reactions } : m}
                  myUid={uid}
                  grouped={grouped}
                  parent={parentOf(m)}
                  profiles={profiles}
                  urls={isPending ? pendingUrls : urls}
                  highlighted={highlightId === m.id}
                  pending={m.pendingState}
                  onOpenActions={setActionsFor}
                  onReply={setReplyTo}
                  onReact={react}
                  onJumpTo={setJumpTarget}
                  onRetry={
                    isPending
                      ? () => {
                          const p = pending.find((x) => x.tempId === m.pendingTempId);
                          if (p) void send(p.composed, p);
                        }
                      : undefined
                  }
                  onDiscard={
                    isPending
                      ? () => setPending((list) => list.filter((x) => x.tempId !== m.pendingTempId))
                      : undefined
                  }
                />
              </Fragment>
            );
          })}
        </div>

        <AnimatePresence>
          {!atBottom && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              onClick={() => scrollToBottom(true)}
              className="absolute bottom-3 right-3 flex h-10 items-center gap-1.5 rounded-full border border-border bg-surface px-3 text-sm shadow-lg sm:right-5"
              aria-label="Jump to latest"
            >
              <ArrowDown className="h-4 w-4" />
              {newBelow > 0 && <span className="font-medium text-accent">{newBelow} new</span>}
            </motion.button>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {actionError && (
            <motion.p
              role="status"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-x-3 bottom-3 rounded-lg bg-danger px-3 py-2 text-center text-sm text-[var(--ink-0)] shadow-lg sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2"
            >
              {actionError}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <Composer
        projectId={projectId}
        participants={participants}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        editing={editing}
        onCancelEdit={() => setEditing(null)}
        onSubmit={(composed) => void send(composed)}
        onEdit={saveEdit}
        disabledReason={project?.status === 'archived' ? 'This project is archived.' : null}
      />

      <BottomSheet
        open={!!actionsMessage}
        onOpenChange={(open) => !open && setActionsFor(null)}
        title="Message actions"
      >
        {actionsMessage && (
          <div className="flex flex-col pb-2 md:py-2">
            <div className="flex justify-around px-3 pb-2 pt-1">
              {DISCUSSION_REACTION_IDS.map((id) => {
                const mine = (actionsMessage.reactions[id] ?? []).includes(uid);
                return (
                  <button
                    key={id}
                    type="button"
                    aria-label={`React ${DISCUSSION_REACTIONS[id]}`}
                    aria-pressed={mine}
                    onClick={() => {
                      react(actionsMessage, id);
                      setActionsFor(null);
                    }}
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-full text-2xl transition-transform active:scale-90',
                      mine ? 'bg-accent-soft' : 'bg-surface-2',
                    )}
                  >
                    {DISCUSSION_REACTIONS[id]}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-border" />
            <SheetAction
              icon={Reply}
              label="Reply"
              onSelect={() => {
                setEditing(null);
                setReplyTo(actionsMessage);
                setActionsFor(null);
              }}
            />
            {actionsMessage.body && (
              <SheetAction
                icon={Copy}
                label="Copy text"
                onSelect={() => {
                  void navigator.clipboard?.writeText(actionsMessage.body);
                  setActionsFor(null);
                }}
              />
            )}
            <SheetAction
              icon={actionsMessage.pinnedAt ? PinOff : Pin}
              label={actionsMessage.pinnedAt ? 'Unpin' : 'Pin for everyone'}
              onSelect={() => {
                void togglePin(actionsMessage);
                setActionsFor(null);
              }}
            />
            {isOwn && (
              <>
                <SheetAction
                  icon={Pencil}
                  label="Edit"
                  onSelect={() => {
                    setReplyTo(null);
                    setEditing(actionsMessage);
                    setActionsFor(null);
                  }}
                />
                <SheetAction
                  icon={Trash2}
                  label="Delete"
                  destructive
                  onSelect={() => {
                    setConfirmDelete(actionsMessage);
                    setActionsFor(null);
                  }}
                />
              </>
            )}
          </div>
        )}
      </BottomSheet>

      <BottomSheet open={showPinned} onOpenChange={setShowPinned} title="Pinned messages">
        <div className="flex flex-col px-2 pb-3 md:pt-3">
          <h2 className="flex items-center gap-2 px-3 pb-2 text-base font-semibold">
            <Pin className="h-4 w-4 text-warning" /> Pinned
          </h2>
          {pinned.length === 0 && (
            <p className="px-3 pb-4 text-sm text-muted">
              Nothing pinned yet. Long-press a message (or use its ⋯ menu) and choose Pin to keep it
              here for everyone.
            </p>
          )}
          {pinned.map((m) => (
            <div
              key={m.id}
              className="flex items-start gap-2 rounded-lg px-3 py-2.5 hover:bg-surface-2"
            >
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => {
                  setShowPinned(false);
                  setJumpTarget(m.id);
                }}
              >
                <span className="flex items-baseline gap-2 text-xs text-muted">
                  <UserName
                    uid={m.authorUid}
                    showStatus={false}
                    className="font-medium text-text"
                  />
                  {format(asDate(m.createdAt), 'MMM d')}
                </span>
                <span className="line-clamp-2 text-sm">{plainExcerpt(m)}</span>
              </button>
              <button
                type="button"
                aria-label="Unpin"
                onClick={() => void togglePin(m)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted hover:text-text"
              >
                <PinOff className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </BottomSheet>

      <CatchUpSheet
        open={showCatchUp}
        onOpenChange={setShowCatchUp}
        projectId={projectId}
        since={entryLastRead ?? null}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogHeader>
          <DialogTitle>Delete this message?</DialogTitle>
        </DialogHeader>
        <p className="mt-2 text-sm text-muted">
          It's removed for everyone, along with its attachments. This can't be undone.
        </p>
        <DialogFooter className="mt-4">
          <Button className="h-10 md:h-8" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="h-10 md:h-8"
            onClick={() => confirmDelete && void deleteMessage(confirmDelete)}
          >
            Delete
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

/** Reactions with empty lists dropped and keys sorted, so two copies compare equal by value. */
function normalize(reactions: DiscussionMessage['reactions']) {
  return Object.fromEntries(
    Object.entries(reactions)
      .filter(([, uids]) => uids && uids.length > 0)
      .map(([k, uids]) => [k, [...(uids ?? [])].sort()])
      .sort(([a], [b]) => String(a).localeCompare(String(b))),
  );
}
